import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { userService } from '../../../services/userService';
import { User } from '../../../types';
import { useToast } from '../../../components/Toast';

export const useMembers = () => {
    const { t } = useTranslation('common');
    const { showToast } = useToast();

    const [members, setMembers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalMembersCount, setTotalMembersCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState<string>('all');

    const fetchMembers = useCallback(async () => {
        setLoading(true);
        try {
            const data = await userService.listPublicUsers({
                page,
                per_page: 50,
                role: filterRole !== 'all' ? filterRole : undefined,
                q: searchTerm || undefined,
            });
            setMembers(data.users);
            setTotalPages(data.pages);
            setTotalMembersCount(data.total);
        } catch (error: any) {
            console.error('Error fetching members:', error);
            showToast(error?.response?.data?.error || t('checkView.messages.error'), 'error');
        } finally {
            setLoading(false);
        }
    }, [page, filterRole, searchTerm, showToast, t]);

    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    const handleSearch = (term: string) => {
        setSearchTerm(term);
        setPage(1); // Reset to page 1 on search
    };

    const handleRoleFilterChange = (role: string) => {
        setFilterRole(role);
        setPage(1);
    };

    return {
        members,
        loading,
        page,
        setPage,
        totalPages,
        totalMembersCount,
        searchTerm,
        handleSearch,
        filterRole,
        handleRoleFilterChange,
        refresh: fetchMembers
    };
};
